/**
 * "No BNB" guide (Bez, 2026-10-07): how much BNB the next transaction needs for its network fee, and whether a wallet error means
 * the wallet has too little BNB. UI only: nothing here sends, signs, approves or changes a transaction. CADINU does not pay gas.
 */
export type GasAction = 'swap' | 'trade' | 'competitionCreate' | 'lock'

/**
 * Typical gas units of each action, from BNB Smart Chain receipts read 2026-10-07 and rounded up:
 *  swap               MultiSwap via the PancakeSwap V2 router 221 772 (x4); a multi-hop V3 route of the Smart Router costs more -> 350 000
 *  trade              competition pool trade, one hop through one pool (PoolTrade) -> 250 000
 *  competitionCreate  startCompetition 715 341 (tx 0x81e0b1ca…) plus one ERC-20 approval (~46 000) -> 800 000
 *  lock               V3 position lock 583 889, V2 vesting lock 663 310 -> 700 000
 */
export const TYPICAL_GAS: Record<GasAction, bigint> = {
  swap: 350_000n,
  trade: 250_000n,
  competitionCreate: 800_000n,
  lock: 700_000n,
}
/** The need is gas price x typical gas x 1.5, so a busier block or a longer route still fits. */
export const SAFETY_MARGIN_PCT = 150n
/** When the node does not answer the gas price: 0.1 gwei, twice BNB Smart Chain's 0.05 gwei minimum. */
export const FALLBACK_GAS_PRICE = 100_000_000n

export const gasNeedWei = (action: GasAction, gasPriceWei: bigint): bigint => (gasPriceWei * TYPICAL_GAS[action] * SAFETY_MARGIN_PCT) / 100n

export function bnbNeed({ action, gasPriceWei, extraWei = 0n }: { action: GasAction; gasPriceWei: bigint; extraWei?: bigint }) {
  const gasWei = gasNeedWei(action, gasPriceWei)
  const extra = extraWei > 0n ? extraWei : 0n
  return { gasWei, extraWei: extra, totalWei: gasWei + extra }
}

/** Short only when the balance is known: an unread balance never opens the guide. */
export const isShort = (haveWei: bigint | undefined, needWei: bigint): boolean => haveWei !== undefined && haveWei < needWei

/**
 * What wallets and nodes say when the account cannot pay the gas (and the BNB value) of a transaction:
 *  viem InsufficientFundsError ("... exceeds the balance of the account."), geth / BSC "insufficient funds for gas * price + value",
 *  "insufficient funds for transfer", a BSC node's estimate with an empty account "gas required exceeds allowance (0)",
 *  and the lock tool's own wording of it (views/LockTools/logic/errors.ts).
 * A token shortage ("ERC20: transfer amount exceeds balance") is NOT a gas problem and does not match.
 */
const NO_GAS = /insufficient funds|InsufficientFundsError|exceeds the balance of the account|gas required exceeds allowance|insufficient balance (for|to pay) (gas|fee)|not enough BNB to pay the network fee/i

const textOf = (e: unknown, depth = 0): string => {
  if (e === null || e === undefined || depth > 4) return ''
  if (typeof e === 'string') return e
  if (typeof e !== 'object') return String(e)
  const o = e as Record<string, unknown>
  const own = ['name', 'shortMessage', 'message', 'details', 'reason'].map((k) => (typeof o[k] === 'string' ? (o[k] as string) : '')).join(' | ')
  return `${own} | ${textOf(o.cause, depth + 1)} | ${textOf(o.error, depth + 1)}`
}

export const isNoGasError = (e: unknown): boolean => NO_GAS.test(textOf(e))

export const toBnb = (wei: bigint): number => Number(wei / 10n ** 9n) / 1e9 + Number(wei % 10n ** 9n) / 1e18

/** "0.0000525 BNB": three significant digits, no scientific notation. */
export function fmtBnb(wei: bigint): string {
  if (wei <= 0n) return '0 BNB'
  const n = toBnb(wei)
  if (n >= 1) return `${n.toLocaleString('en-US', { maximumFractionDigits: 4 })} BNB`
  const digits = Math.min(18, Math.max(2, -Math.floor(Math.log10(n)) + 2))
  return `${n.toFixed(digits).replace(/0+$/, '').replace(/\.$/, '')} BNB`
}

/** "$0.60", "less than $0.01", or null without a price. */
export function usdText(wei: bigint, bnbUsd: number | undefined): string | null {
  if (!bnbUsd || !Number.isFinite(bnbUsd) || bnbUsd <= 0) return null
  const usd = toBnb(wei) * bnbUsd
  if (usd < 0.01) return 'less than $0.01'
  return `$${usd.toFixed(2)}`
}

export function needHeadline(usd: string | null, withFee: boolean): string {
  if (withFee) return usd ? `You need ${usd.startsWith('less') ? usd : `about ${usd}`} in BNB for this step.` : 'You need a little more BNB for this step.'
  if (!usd) return 'You need a little BNB for network fees.'
  return `You need a little BNB (${usd.startsWith('less') ? usd : `about ${usd}`}) for network fees.`
}
